<template>
<div>
    <message-card class="mb-3" state="bad" :message="error_message"></message-card>

    <card>
        <template slot="header">
            <div class="flex jc-sb">
                <div class="flex ai-c flex-a mr-2">
                    <i class="material-icons">people</i>
                    <strong class="ml-2">Tenant List</strong>

                    <span class="hint ml-2 mr-2">|</span>

                    <button
                        :disabled="last_row_count === 0 || is_loading_export"
                        class="icon"
                        title="Export"
                        @click="exportData">
                        <i class="material-icons">save_alt</i>
                    </button>

                    <img v-show="is_loading_export" class="ml-3" src="../../assets/img/loading-black-32.gif" alt="loading" />

                    <span v-show="export_download_path !== null" class="ml-3">
                        <a :href="export_download_path" target="_blank">Download</a>
                        <button class="text ml-6" @click="export_filename = null">
                            <i class="material-icons">clear</i>
                        </button>
                    </span>
                </div>

                <div class="flex-0 ml-2">
                    <button :disabled="is_loading_tenants" class="icon" type="button" @click="$refs.tenants_table.reload()">
                        <i class="material-icons">refresh</i>
                    </button>
                </div>
            </div>
        </template>

        <template slot="body">
            <div>
                <div class="mb-3">
                    <p>You are unable to edit tenants from this page.<br />To make tenant status changes, you must either go home and click on a cycle's audit button, or click on a tenant's cycles link below.</p>
                </div>

                <div>
                    <div class="flex ai-c jc-sb">
                        <div class="flex-0 mr-3 ws-nw">Filter tenants:</div>

                        <select-input
                            :active-index="active_filter_index"
                            :choices="filters"
                            :disabled="is_loading_tenants"
                            @change="(index) => active_filter_index = index"></select-input>
                    </div>

                    <div class="flex ai-c jb-sb mt-5">
                        <div class="flex-1" style="width: 100px;">
                            <div v-if="data_info && data_info.incomplete_address > 0" class="danger-text flex ai-c mt-5">
                                <span class="mr-2"><i class="material-icons">announcement</i></span>

                                <span v-if="data_info.incomplete_address === 1">There is 1 tenant with an incomplete address!</span>
                                <span v-else>There are {{ data_info.incomplete_address.toLocaleString() }} tenants with an incomplete address!</span>
                            </div>
                            <div v-else>&nbsp;</div>
                        </div>

                        <div class="flex-auto">
                            <vue-table-pagination
                                ref="tenants_table_pagination"
                                @vuetable-pagination:change-page="onChangePage">
                            </vue-table-pagination>
                        </div>

                        <div class="flex-1 ta-r" style="width: 100px;">
                        </div>
                    </div>
                </div>

                <div class="mt-5">
                    <div :class="['table-wrapper', 'table-loads', { loading: is_loading_tenants }]">
                        <div class="table-error" v-show="load_tenants_error">
                            <span>{{ load_tenants_error }}</span>
                            <button class="text mt-2" :disabled="is_loading_tenants" @click="$refs.tenants_table.reload()">Retry?</button>
                        </div>

                        <vue-table ref="tenants_table"
                            :load-on-start="false"
                            :api-url="tenants_api_url"
                            :append-params="api_params"
                            data-path="data.data"
                            pagination-path="data.pagination"
                            :per-page="100"
                            table-height="475px"
                            :http-options="http_options"
                            :fields="columns"
                            :data-manager="manageTableFunc"
                            no-data-template="<span>No tenants to show.</span>"
                            @vuetable:pagination-data="onPaginationData"
                            @vuetable:loading="is_loading_tenants = true"
                            @vuetable:loaded="is_loading_tenants = false"
                            @vuetable:load-success="onTableLoadSuccess"
                            @vuetable:load-error="onTableError">
                            <template slot="name" slot-scope="props">
                                <div class="flex jc-sb ai-c">
                                    <span>{{ props.rowData.last_name }}, {{ props.rowData.first_name }}</span>

                                    <span v-show="props.rowData.welcome_letter_sent_date !== null" title="Locked" class="success-text cu-d">
                                        <i class="material-icons">lock</i>
                                    </span>
                                </div>
                            </template>

                            <template slot="cycle" slot-scope="props">
                                <div v-if="props.rowData.cycle_is_active">
                                    <router-link :to="`/cycle-audit/${props.rowData.cycle_id}`">
                                        <span class="badge">{{ props.rowData.cycle_label }}</span>
                                    </router-link>
                                </div>

                                <div v-else>
                                    <span class="badge gray">{{ props.rowData.cycle_label }}</span>
                                </div>
                            </template>

                            <template slot="billing_date" slot-scope="props">
                                <div class="pos-r">
                                    <span v-html="formatDateFunc(props.rowData.billing_date)"></span>

                                    <span class="cu-d pos-a r-0 vertically-center success-text"
                                        title="Anniversary billing"
                                        v-if="props.rowData.billing_anniversary == true">
                                        <i class="material-icons">today</i>
                                    </span>
                                </div>
                            </template>

                            <template slot="charge_apply_date" slot-scope="props">
                                <span v-html="formatDateFunc(props.rowData.charge_apply_date)"></span>
                            </template>

                            <template slot="charge_added" slot-scope="props">
                                <span class="success-text" v-if="props.rowData.charge_applied_date !== null"
                                    :title="'Added ' + props.rowData.charge_applied_date">
                                    <i class="material-icons">check_circle</i>
                                </span>
                            </template>

                            <template slot="has_insurance" slot-scope="props">
                                <span class="success-text" v-if="props.rowData.coverage_id !== null">
                                    <i class="material-icons">check_circle</i>
                                </span>
                            </template>

                            <template slot="status" slot-scope="props">
                                <div v-if="props.rowData.status === null" class="gray-message">No response</div>

                                <div v-else class="flex ai-c jc-sb">
                                    <div>
                                        <span :class="[props.rowData.status.toLowerCase().replace(' ', '-')]">
                                            {{ props.rowData.status }}
                                        </span>

                                        <strong v-if="props.rowData.tenant_made_selection" class="badge cu-h ml-1" title="Tenant made selection">
                                            T
                                        </strong>
                                    </div>
                                </div>
                            </template>
                        </vue-table>
                    </div>
                </div>
            </div>
        </template>
    </card>
</div>
</template>

<script>
import Vue from "vue"
import http from "@/http"
import store from "@/store"
import { formatDate, masterAuditColumns } from "@/utilities/columns"

export default {
    name: "manage-tenants-page",

    data () {
        return {
            active_filter_index: 0,
            columns: masterAuditColumns,
            data_info: null,
            error_message: "",
            export_filename: null,
            formatDateFunc: formatDate.bind(this),
            http_options: {
                baseURL: this.$http.defaults.baseURL,
                headers: {
                    "X-Authorization": store.state.token.token
                },
                transformResponse: [(raw) => {
                    try {
                        return JSON.parse(raw)
                    } catch (error) {
                        this.load_tenants_error = "Unknown error loading tenants."

                        return raw
                    }
                }]
            },
            is_loading_export: false,
            is_loading_tenants: false,
            last_row_count: 0,
            load_tenants_error: "",
            manageTableFunc: this.manageTable.bind(this),
            sort_field: ""
        }
    },

    mounted () {
        this.loadData()
    },

    computed: {
        api_params () {
            const params = {}

            const filter = this.filters[this.active_filter_index]
            if (filter) {
                params.filter = filter.key
            }

            return params
        },

        export_download_path () {
            if (this.export_filename === null) {
                return null
            }

            let path = `${this.$http.defaults.baseURL}/api/exports/download`

            path += `?file_key=${this.export_filename}`
            path += "&filename=tenant_list"

            return path
        },

        facility () {
            return store.state.userFacility.facility
        },

        filters () {
            const filters = store.state.userFacility.filters

            return filters.map((filter) => {
                filter.badge = filter.count

                return filter
            })
        },

        tenants_api_url () {
            const user = store.state.user

            if (user) {
                return `/api/facilities/${user.facility_id}/tenants`
            } else {
                return null
            }
        }
    },

    watch: {
        active_filter_index () {
            Vue.nextTick(() => {
                this.$refs.tenants_table.reload()
            })
        }
    },

    methods: {
        async loadData () {
            this.getFilters()

            if (this.facility) {
                this.getTenants()

                await store.dispatch("userFacility/loadFacility")

                this.normalizeDates()
            } else {
                await store.dispatch("userFacility/loadFacility")

                this.getTenants()
                this.normalizeDates()
            }
        },

        async getFilters () {
            store.dispatch("userFacility/loadFilters")
        },

        async getTenants () {
            this.$refs.tenants_table.reload()
        },

        async exportData () {
            if (this.facility) {
                this.is_loading_export = true

                const params = {}

                const filter = this.filters[this.active_filter_index]
                if (filter) {
                    params.filter = filter.key
                }

                try {
                    const { data: response } = await http("/api/exports/manager-tenants", { params })

                    this.export_filename = response.filename
                } catch (ex) {
                    console.dir(ex)
                } finally {
                    this.is_loading_export = false
                }
            }
        },

        /**
         * Set the sort field when the user requests sorting.
         *
         * @param object data Row data
         */
        manageTable (data) {
            if (data.length) {
                this.sort_field = {
                    field: data[0].sortField,
                    direction: data[0].direction
                }
            } else {
                this.sort_field = {}
            }
        },

        /**
         * Normalize the letter send dates because JavaScript automatically subtracts hours
         * for local time, making it appear as if the date selected is the day before.
         */
        normalizeDates () {
            if (this.facility.initial_letter_send_date) {
                const date = new Date(this.facility.initial_letter_send_date)
                date.setUTCHours(date.getHours())
                this.facility.initial_letter_send_date = date
            }

            if (this.facility.reminder_letter_send_date) {
                const date = new Date(this.facility.reminder_letter_send_date)
                date.setUTCHours(date.getHours())
                this.facility.reminder_letter_send_date = date
            }
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.tenants_table_pagination.setPaginationData(paginationData)
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.tenants_table.changePage(page)
        },

        onTableError (error) {
            const { data: { data: message } } = error.response

            this.$eventManager.emit("add-toast", { message: `Error! ${message}`, type: "error" })
        },

        /**
         * Get the loaded record count when the table successfully loads data.
         *
         * @param {object} data Server response data object
         */
        onTableLoadSuccess ({ data: { data: response } }) {
            this.last_row_count = response.data.length
            this.data_info = response.info
        }
    }
}
</script>

<style>
.facility-table tr + tr td {
    padding-top: 5px;
}

.facility-table td {
    vertical-align: top;
}

.facility-table td:first-child {
    text-align: right;
    padding-right: 15px;
}
</style>



// WEBPACK FOOTER //
// src/pages/manager/MasterAuditPage.vue